import { useState } from "react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { api } from "../lib/api";
import {
  ShieldCheck, FileSearch, GaugeCircle, KanbanSquare, Users2, LineChart,
  ArrowRight, CheckCircle2, Loader2, Award, Linkedin,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: GaugeCircle,
    title: "Investment readiness, scored in minutes",
    body: "Every portfolio company gets a Clinq Capital Readiness Score (CCR Score, 0–100) across six funder-grade pillars — pitch, financials, team, market, structure and impact. Cohort-wide readiness assessments that once took weeks of analyst time run automatically.",
  },
  {
    icon: FileSearch,
    title: "AI document extraction for due diligence",
    body: "Founders upload their pitch deck, financial model and data room documents — Clinq's AI extracts, verifies and gap-checks them against what investors will ask for, surfacing red flags in corporate and financial structure before formal due diligence starts.",
  },
  {
    icon: ShieldCheck,
    title: "Data rooms elevated to investor standard",
    body: "Each company receives a personalized Capital Readiness Report: fixable gaps, the exact score lift per fix, and a 90-day roadmap. Materials reach due-diligence standard faster — and you can see exactly what improved.",
  },
  {
    icon: KanbanSquare,
    title: "Portfolio tracking & monthly progress",
    body: "A live pipeline dashboard tracks every company through a staged fundraising journey — documentation → first score → report → improvement → funder matching — so monthly progress reporting against KPIs becomes a glance, not a chore.",
  },
  {
    icon: Users2,
    title: "Tailored investor matching",
    body: "Companies are matched against real funder mandates — VCs, DFIs, angels, CVCs and debt providers — with a compatibility percentage per deal, aligned to sector, stage, ticket size and geography. Warm introductions follow when companies hit Market Ready.",
  },
  {
    icon: LineChart,
    title: "Founder pipeline management",
    body: "Founders get their own deal pipeline and coaching from David, Clinq's AI advisor — tracking investor conversations, keeping documents fresh, and transferring the skills to run future raises independently.",
  },
];

const PORTFOLIO_SIZES = ["1–5 companies", "6–15 companies", "16–50 companies", "50+ companies"];

export default function VentureStudios() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", portfolio_size: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/studios/demo-request", form);
      setSent(true);
    } catch (_e) {
      setError("Something went wrong — please try again or email capital@clinq.africa.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />
      {/* Hero */}
      <section className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300/90">Clinq for Venture Studios & Accelerators</p>
          <h1 data-testid="studios-hero-title" className="mt-5 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            How Clinq Supports Venture Studios <span className="text-emerald-400">and Accelerators</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-white/85 sm:text-lg">
            Your cohorts graduate — then the real work starts: investment readiness assessments, data-room
            reviews, monthly progress reports and investor introductions across a whole portfolio. Clinq's
            AI-powered Readiness Score and document extraction automate the due diligence and portfolio
            tracking, so your team focuses on closing rounds — from Seed to Series A.
          </p>
          <a href="#studio-demo" data-testid="studios-hero-cta"
             className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667] sm:w-auto">
            Get a Studio Demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </section>

      {/* Capabilities mapped to accelerator needs */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Built for post-program fundraising support</p>
        <h2 className="mt-3 max-w-2xl text-base font-bold text-neutral-900 md:text-lg">
          Everything an alumni fundraising mandate asks for — readiness assessment, materials refinement,
          due diligence, pipeline management and reporting — in one platform.
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c) => (
            <div key={c.title} data-testid={`studios-capability-${c.title.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                 className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
              <c.icon className="h-6 w-6 text-[#0A8B85]" />
              <h3 className="mt-4 text-sm font-extrabold text-neutral-900">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Impact accelerators — engagement structure */}
      <section className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300/90">For impact-driven accelerators & funds</p>
          <h2 data-testid="studios-impact-heading" className="mt-3 max-w-3xl text-base font-bold md:text-lg">
            Built for programs moving impact capital from the margins to the mainstream — climate-tech,
            agri-tech, ocean and beyond.
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              ["Cohort engagements", "Support a select group of alumni — e.g. 4 companies over 6 months — with per-company readiness roadmaps from day one."],
              ["Individual assessment reports", "Each founder starts with a scored assessment and a personalized fundraising roadmap you can share with your investment committee."],
              ["Monthly progress reporting", "Stage-by-stage tracking against agreed KPIs and milestones — exportable, board-ready, no analyst hours burned."],
              ["Final impact summary", "Close the engagement with a portfolio-wide summary: score lifts, gaps closed, introductions made and rounds in motion."],
            ].map(([t, b]) => (
              <div key={t} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-extrabold text-emerald-300">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-sm text-white/70">
            Sector fluency where impact mandates live: our funder network and scoring pillars cover
            climate-tech, agri-tech, energy and blue-economy ventures, with mandate-matched access to
            DFIs, impact VCs, angels, CVCs and debt providers active across Africa.
          </p>
        </div>
      </section>

      {/* Experts */}
      <section className="border-y border-[#E8DFC5] bg-[#FBF7EC]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">
            <Award className="h-4 w-4" /> Human expertise behind the AI
          </div>
          <h2 data-testid="studios-experts-heading" className="mt-3 max-w-3xl text-base font-bold text-neutral-900 md:text-lg">
            Clinq pairs its platform with a bench of fundraising experts with over a decade of experience —
            who have helped startups raise <span className="text-[#B45309]">over USD 25M+ in the last three years</span>,
            from Seed to Series A, across the African ecosystem.
          </h2>
          <ul className="mt-6 grid max-w-3xl gap-2 text-sm text-neutral-700">
            {["Deal-by-deal support on term sheets, valuation and equity/debt structuring",
              "Warm introductions to an active network of VCs, DFIs, angels, CVCs and debt providers",
              "Founder coaching that transfers negotiation skills — not dependency"].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0A8B85]" /> {t}
              </li>
            ))}
          </ul>
          <div data-testid="studios-expert-doreen" className="mt-8 flex max-w-xl flex-col gap-4 rounded-2xl border border-[#E8DFC5] bg-white p-6 sm:flex-row sm:items-center">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#04302E] text-lg font-extrabold text-emerald-300">DZ</div>
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-neutral-900">Doreen Zaki</p>
              <p className="text-xs font-semibold text-[#0A8B85]">Founder, Clinq · Lead Fundraising Expert</p>
              <p className="mt-1 text-sm text-neutral-600">
                Founder of Clinq and lead fundraising expert with over a decade of experience preparing
                African founders for institutional capital. Doreen has led investment readiness assessments,
                investor strategy and warm introductions across Seed to Series A rounds — part of the Clinq
                expert bench that has helped startups raise over USD 25M+ in the last three years — and works
                hands-on with accelerator cohorts on data rooms, financial models and term-sheet negotiation.
              </p>
              <a href="https://www.linkedin.com/in/doreen-zaki/" target="_blank" rel="noreferrer"
                 data-testid="studios-expert-linkedin"
                 className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#0A8B85] hover:underline">
                <Linkedin className="h-3.5 w-3.5" /> Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Demo form */}
      <section id="studio-demo" className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Get a Studio Demo</p>
        <h2 className="mt-3 text-base font-bold text-neutral-900 md:text-lg">
          For fund managers, venture studios and accelerator teams — see how Clinq would run readiness,
          due diligence and portfolio tracking for your cohort.
        </h2>
        {sent ? (
          <div data-testid="studio-demo-success" className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-sm text-neutral-800">
            <p className="font-extrabold text-emerald-700">Request received — thank you!</p>
            <p className="mt-1">Our team will reach out within one business day to schedule your demo.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="studio-demo-form">
            <input required value={form.name} onChange={set("name")} placeholder="Your name"
                   data-testid="studio-demo-name"
                   className="rounded-md border border-neutral-300 px-4 py-3 text-sm focus:border-[#0A8B85] focus:outline-none" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Work email"
                   data-testid="studio-demo-email"
                   className="rounded-md border border-neutral-300 px-4 py-3 text-sm focus:border-[#0A8B85] focus:outline-none" />
            <input required value={form.organization} onChange={set("organization")} placeholder="Studio / accelerator / fund name"
                   data-testid="studio-demo-org"
                   className="rounded-md border border-neutral-300 px-4 py-3 text-sm focus:border-[#0A8B85] focus:outline-none" />
            <input value={form.role} onChange={set("role")} placeholder="Your role (e.g. Fund Manager)"
                   data-testid="studio-demo-role"
                   className="rounded-md border border-neutral-300 px-4 py-3 text-sm focus:border-[#0A8B85] focus:outline-none" />
            <select value={form.portfolio_size} onChange={set("portfolio_size")}
                    data-testid="studio-demo-portfolio"
                    className="rounded-md border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-700 focus:border-[#0A8B85] focus:outline-none sm:col-span-2">
              <option value="">Portfolio size (optional)</option>
              {PORTFOLIO_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <textarea value={form.message} onChange={set("message")} rows={4}
                      data-testid="studio-demo-message"
                      placeholder="Tell us about your cohort and what you need — e.g. readiness assessments for 4 alumni raising Seed to Series A"
                      className="rounded-md border border-neutral-300 px-4 py-3 text-sm focus:border-[#0A8B85] focus:outline-none sm:col-span-2" />
            {error && <p data-testid="studio-demo-error" className="text-sm text-red-600 sm:col-span-2">{error}</p>}
            <button type="submit" disabled={busy} data-testid="studio-demo-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667] disabled:opacity-60 sm:col-span-2 sm:w-auto">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Request my Studio Demo
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
